<template>
  <section id="about" class="about-section">
    <div class="container">
      <div class="section-title">
        <div class="en-label">{{ $t('about.eyebrow') }}</div>
        <h2>{{ $t('about.titlePrefix') }} <span class="gradient-text">{{ $t('about.titleHighlight') }}</span></h2>
        <p>{{ $t('about.desc') }}</p>
      </div>

      <div class="about-grid">
        <div class="about-visual">
          <div class="visual-card">
            <div class="visual-bg"></div>
            <div class="visual-content">
              <div class="company-badge">
                <div class="badge-icon">✚</div>
                <div>
                  <div class="badge-title">{{ $t('about.badgeTitle') }}</div>
                  <div class="badge-sub">{{ $t('about.badgeSub') }}</div>
                </div>
              </div>
              <div class="milestone-list">
                <div class="milestone" v-for="m in milestones" :key="m.id">
                  <div class="m-year">{{ m.year }}</div>
                  <div class="m-dot"></div>
                  <div class="m-text">{{ m.text }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="about-text">
          <div class="value-cards">
            <div class="value-card" v-for="v in values" :key="v.id">
              <div class="value-icon" :style="{ background: v.bg }">
                <i :class="v.icon"></i>
              </div>
              <div>
                <h4>{{ v.title }}</h4>
                <p>{{ v.desc }}</p>
              </div>
            </div>
          </div>

          <div class="about-mission">
            <div class="mission-item" v-for="item in mission" :key="item.id">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const valueMeta = {
  ai: {
    icon: 'el-icon-cpu',
    bg: 'linear-gradient(135deg,#6c5ce7,#a29bfe)'
  },
  security: {
    icon: 'el-icon-lock',
    bg: 'linear-gradient(135deg,#00c9a7,#00b894)'
  },
  ecosystem: {
    icon: 'el-icon-connection',
    bg: 'linear-gradient(135deg,#fd79a8,#e84393)'
  }
}

export default {
  name: 'AboutSection',
  computed: {
    milestones() {
      return this.$tm('about.milestones')
    },
    values() {
      return this.$tm('about.values').map(value => ({
        ...value,
        ...valueMeta[value.id]
      }))
    },
    mission() {
      return this.$tm('about.mission')
    }
  }
}
</script>

<style lang="less" scoped>
.about-section { padding: 120px 0; background: #fff; }
.about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: start; }
.about-visual { position: sticky; top: 100px; }
.visual-card { border-radius: 24px; overflow: hidden; position: relative; padding: 36px; background: #050d1a; box-shadow: var(--card-shadow); }
.visual-bg { position: absolute; inset: 0; background: linear-gradient(135deg,#050d1a 0%,#0d1f3c 60%,#050d1a 100%); }
.visual-bg:before { content: ''; position: absolute; inset: 0; background-image: linear-gradient(rgba(0,201,167,.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,201,167,.04) 1px, transparent 1px); background-size: 40px 40px; }
.visual-content { position: relative; z-index: 1; }
.company-badge { display: flex; align-items: center; gap: 14px; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 1px solid rgba(255,255,255,.08); }
.badge-icon { width: 48px; height: 48px; border-radius: 14px; background: linear-gradient(135deg,#0a5c8a,#00c9a7); display: flex; align-items: center; justify-content: center; font-size: 22px; color: #fff; font-weight: bold; flex-shrink: 0; }
.badge-title { font-size: 18px; font-weight: 700; color: #fff; }
.badge-sub { font-size: 12px; color: rgba(255,255,255,.45); margin-top: 2px; }
.milestone { display: flex; align-items: flex-start; gap: 14px; padding: 18px 0; position: relative; }
.m-year { font-size: 14px; font-weight: 800; color: #00c9a7; width: 56px; flex-shrink: 0; line-height: 1.4; }
.m-dot { width: 8px; height: 8px; border-radius: 50%; background: #00c9a7; flex-shrink: 0; margin-top: 6px; box-shadow: 0 0 8px rgba(0,201,167,.6); }
.m-text { font-size: 13px; color: rgba(255,255,255,.72); line-height: 1.7; }
.value-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 40px; }
.value-card { display: flex; align-items: flex-start; gap: 14px; background: var(--bg-light); border-radius: var(--radius); padding: 20px; transition: box-shadow .2s, transform .2s; min-height: 104px; }
.value-card:hover { box-shadow: var(--card-shadow); transform: translateY(-2px); }
.value-icon { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 20px; flex-shrink: 0; }
.value-card h4 { font-size: 14px; font-weight: 700; color: var(--text-dark); margin-bottom: 6px; }
.value-card p { font-size: 12px; color: var(--text-light); line-height: 1.6; }
.about-mission { background: linear-gradient(135deg,#050d1a,#0d1f3c); border-radius: var(--radius-lg); padding: 32px; margin-bottom: 24px; }
.about-mission h3 { font-size: 16px; font-weight: 700; color: #00c9a7; margin: 0 0 12px; }
.about-mission p { font-size: 14px; color: rgba(255,255,255,.78); line-height: 1.8; margin-bottom: 18px; }
.mission-item:last-child p { margin-bottom: 0; }
@media (max-width: 900px) { .about-grid { grid-template-columns: 1fr; gap: 50px; } .about-visual { position: static; } }
@media (max-width: 480px) { .value-cards { grid-template-columns: 1fr; } }
</style>
